<template>
  <SDialog
    id="cancel-order-dialog"
    :show-dialog="modelValue"
    :width="380"
    sticky-close
    data-testid="cancel-order-dialog"
    :aria-close="$t('dialog.close.aria-label')"
    @update:show-dialog="onShowDialog"
  >
    <template v-if="status === 'success'">
      <h3 class="font-weight-medium text-uppercase px-4 pt-10 pb-1">
        {{ $t('dialog.header.cancelSubmitted') }}
      </h3>
      <span class="d-block px-4 py-2">
        {{ $t('dialog.p.cancelSubmitted') }}
      </span>
      <VRow no-gutters justify="center" class="px-4">
        <VCol cols="12" class="py-2">
          <SBtn
            action
            block
            color="primary"
            class="mb-3"
            data-testid="cancel-order-close"
            @click="close"
          >
            {{ $t('input.btn.close') }}
          </SBtn>
        </VCol>
      </VRow>
    </template>

    <template v-else>
      <!-- role="alert" so a failed submit is announced (WCAG 4.1.3). Each banner is inserted
           on failure and removed while submitting, so a repeat failure is announced again. -->
      <div
        v-if="status === 'error'"
        class="error-banner"
        role="alert"
        data-testid="cancel-order-error"
      >
        {{ $t('dialog.error.cancelOrder.orderEmailNotFound') }}
      </div>
      <div
        v-else-if="status === 'systemError'"
        class="error-banner"
        role="alert"
        data-testid="cancel-order-system-error"
      >
        {{ $t('error.p.500errorLine1') }}
      </div>

      <h3 class="font-weight-medium text-uppercase px-4 pb-1" :class="hasBanner ? 'pt-5' : 'pt-10'">
        {{ $t('dialog.header.cancelOrder') }}
      </h3>
      <span class="d-block px-4 py-2">
        {{ $t('dialog.p.cancelOrder') }}
      </span>

      <ObservedForm noerror @submit="onSubmit">
        <SLoadingOverlay :loading="status === 'submitting'" />
        <VRow no-gutters justify="center" class="px-4">
          <VCol cols="12">
            <TextFieldWithValidation
              v-model="email"
              :hide-details-without-error="true"
              :label="$t('label.email')"
              rules="required|email"
              name="cancel-order-email"
              autocomplete="email"
              required
              data-testid="cancel-order-email"
            />
          </VCol>
          <VCol cols="12" class="py-2">
            <SBtn
              type="submit"
              action
              block
              color="primary"
              :loading="status === 'submitting'"
              data-testid="cancel-order-submit"
            >
              {{ $t('input.btn.submit') }}
            </SBtn>
          </VCol>
        </VRow>
      </ObservedForm>

      <VDivider class="my-3" />
      <span class="d-block px-4 pb-2 font-italic font12" data-testid="cancel-order-note">
        {{ $t('dialog.note.cancelOrder') }}
      </span>
    </template>
  </SDialog>
</template>

<script lang="ts" setup>
import SDialog from '~/components/SDialog.vue'
import TextFieldWithValidation from '~/components/base/fields/TextFieldWithValidation.vue'
import ObservedForm from '~/components/base/ObservedForm.vue'
import { useCancelOrder } from '~/composables/useCancelOrder'

const modelValue = defineModel<boolean>({ required: true })

const { status, submit, reset } = useCancelOrder()
const email = ref('')
const hasBanner = computed(() => status.value === 'error' || status.value === 'systemError')

// Every opening starts clean: a previous success or error must not greet the next attempt.
watch(modelValue, (next, prev) => {
  if (next && !prev) {
    reset()
    email.value = ''
  }
})

// Editing the email after a failed attempt clears the banner so the user can retry.
// Nuxt2 hung this on the field's `@input`; a watch on the model is the same signal
// without depending on how the field wrapper forwards native events.
watch(email, () => {
  if (hasBanner.value) {
    reset()
  }
})

const onSubmit = async (): Promise<void> => {
  await submit(email.value)
}

const onShowDialog = (open: boolean): void => {
  modelValue.value = open
}

const close = (): void => {
  modelValue.value = false
}
</script>

<style lang="scss" scoped>
.error-banner {
  border: 1px solid rgb(var(--v-theme-error));
  color: rgb(var(--v-theme-error));
  padding: 8px 16px;
  margin: 48px 16px 0;
  text-align: left;
}
</style>
